import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { MutableRefObject, useCallback } from 'react';

import styles from './index.module.scss';
import moreIcon from '../../../assets/Home/more.svg';
import npcReleaseIcon from '../../../assets/Home/npcCheck.svg';
import npcDraftIcon from '../../../assets/Home/npcDraft.svg';
import npcFailedIcon from '../../../assets/Home/npcFailed.svg';
import npcPendingIcon from '../../../assets/Home/npcPending.svg';
import {
  WorkspaceNpcInfo,
  WorkspaceNpcStatusInfo,
  ENextVersionStatus,
  EModerationDecision,
} from '../../../types';
import { EWorkspaceNpcStatus } from '../../../types/npc';
import { isTalkie } from '../../../utils';
import { LocalImage } from '../../Image';
import { NpcCard } from '../NpcCard';

interface INpcListProps {
  npcList: WorkspaceNpcInfo[];
  defaultImg: string;
  scrollRef?: MutableRefObject<HTMLDivElement | null>;
  openPermissionModal: (npcInfo: WorkspaceNpcInfo) => void;
  openDeleteModal: (npcInfo: WorkspaceNpcInfo) => void;
}

interface IDropDownMenuProps {
  npcInfo: WorkspaceNpcInfo;
  status: EWorkspaceNpcStatus;
  onOpenPermission: (npcInfo: WorkspaceNpcInfo) => void;
  onDelete: (npcInfo: WorkspaceNpcInfo) => void;
}

export const StatusIconMap: Record<EWorkspaceNpcStatus, any> = {
  [EWorkspaceNpcStatus.Draft]: npcDraftIcon,
  [EWorkspaceNpcStatus.Release]: npcReleaseIcon,
  [EWorkspaceNpcStatus.ReviewFailed]: npcFailedIcon,
  [EWorkspaceNpcStatus.UnderReview]: npcPendingIcon,
};

const StatusTextMap: Record<EWorkspaceNpcStatus, string> = {
  [EWorkspaceNpcStatus.Draft]: 'draft',
  [EWorkspaceNpcStatus.Release]: 'released',
  [EWorkspaceNpcStatus.ReviewFailed]: 'reviewFailed',
  [EWorkspaceNpcStatus.UnderReview]: 'underReview',
};

export function getNpcStatus(status_info: WorkspaceNpcStatusInfo) {
  const { last_submitted_time, next_version_status, final_decision } = status_info;
  if (last_submitted_time === 0) {
    return EWorkspaceNpcStatus.Draft;
  }

  if (next_version_status === ENextVersionStatus.NextVersionDoingModeration) {
    return EWorkspaceNpcStatus.UnderReview;
  }

  if (next_version_status === ENextVersionStatus.NoNextVersion) {
    if (
      final_decision === EModerationDecision.VisibleAll ||
      final_decision === EModerationDecision.VisibleSelf
    ) {
      return EWorkspaceNpcStatus.Release;
    } else {
      return EWorkspaceNpcStatus.ReviewFailed;
    }
  }

  return EWorkspaceNpcStatus.Draft;
}

const DropDownMenu = ({ npcInfo, status, onOpenPermission, onDelete }: IDropDownMenuProps) => {
  const { t } = useTranslation();
  const router = useRouter();

  const onClickPermission = useCallback(
    (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      onOpenPermission(npcInfo);
    },
    [npcInfo, onOpenPermission]
  );

  const onClickDelete = useCallback(
    (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      onDelete(npcInfo);
    },
    [npcInfo, onDelete]
  );

  const onClickCreateCard = useCallback(
    (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      // 跳转到卡牌创建页面
      router.push(`/create/card?npc_id=${npcInfo.npc_id}`);
    },
    [npcInfo, router]
  );

  return (
    <div className="flex flex-col bg-[#2B2C2E] p-[8px] rounded-[16px] text-[#fff]">
      {status === EWorkspaceNpcStatus.Release && (
        <div
          className="flex justify-center cursor-pointer items-center p-[8px] rounded-[8px] w-[136px] h-[28px] text-[14px] hover:bg-[#FFFFFF14]"
          onClick={(e) => {
            onClickPermission(e);
          }}
        >
          {t('permission_setting')}
        </div>
      )}
      {status === EWorkspaceNpcStatus.Release && !isTalkie && (
        <div
          className="flex justify-center cursor-pointer items-center p-[8px] rounded-[8px] w-[136px] h-[28px] text-[14px] hover:bg-[#FFFFFF14]"
          onClick={(e) => {
            onClickCreateCard(e);
          }}
        >
          {'创建星念'}
        </div>
      )}
      <div
        className="flex justify-center cursor-pointer items-center p-[8px] rounded-[8px] w-[136px] h-[28px] text-[14px] hover:bg-[#FFFFFF14] text-[#F55762]"
        onClick={(e) => {
          onClickDelete(e);
        }}
      >
        {status === EWorkspaceNpcStatus.Draft ? t('delete') : t('unbind')}
      </div>
      
    </div>
  );
};

const StatusTab = ({ status }: { status: EWorkspaceNpcStatus }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.npc_status}>
      <LocalImage src={StatusIconMap[status]} width={16} height={16} />
      {t(StatusTextMap[status])}
    </div>
  );
};

export const NpcList = ({
  npcList,
  scrollRef,
  openPermissionModal,
  openDeleteModal,
  defaultImg,
}: INpcListProps) => {
  return (
    <div className={styles.npcList} ref={scrollRef}>
      {npcList.map((item, index) => {
        const status = getNpcStatus(item.status_info);
        return (
          <NpcCard
            key={index}
            styles={styles}
            npcInfo={item}
            status={status}
            dropdownMenu={
              <DropDownMenu
                npcInfo={item}
                status={status}
                onOpenPermission={(npcInfo) => openPermissionModal(npcInfo)}
                onDelete={(npcInfo) => {
                  openDeleteModal(npcInfo);
                }}
              />
            }
            statusTab={<StatusTab status={status} />}
            topRightIcon={
              <div
                className={styles.menu}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <LocalImage src={moreIcon} />
              </div>
            }
            defaultImg={defaultImg}
          />
        );
      })}
    </div>
  );
};
